function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} image - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
export default async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, size = null) {
  // Read Image
  const image = await createImage(imageSrc);

  // Generate Canvas
  const canvas = document.createElement('canvas');

  // Get Context
  const ctx = canvas.getContext('2d');

  // Evaluate need for resize
  let targetWidth, targetHeight;
  if (size) {
    // Get measures
    const measures = size.split('x');

    // Update measures
    targetWidth = parseFloat(measures[0]);
    targetHeight = parseFloat(measures[1]);
  } else {
    // Use crop sizes
    targetWidth = pixelCrop.width;
    targetHeight = pixelCrop.height;
  }

  // Check if rotation is not needed and image fits criteria
  if (rotation === 0 && targetWidth === pixelCrop.width && targetHeight === pixelCrop.height) {
    // Yes, save image as it is,
    // From https://github.com/DominicTobias/react-image-crop/issues/263#issuecomment-517318807
    // Calculate scales
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas measures
    canvas.width = Math.ceil(pixelCrop.width * scaleX);
    canvas.height = Math.ceil(pixelCrop.height * scaleY);

    // Draw
    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY
    );
  } else {
    // No, Crop and rotate needed
    // Calculate safe area
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = Math.ceil(safeArea);
    canvas.height = Math.ceil(safeArea);

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = Math.ceil(pixelCrop.width);
    canvas.height = Math.ceil(pixelCrop.height);

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );
  }

  // As a blob file
  return new Promise((resolve) => {
    canvas.toBlob(
      (file) => {
        // Evaluate need for resize
        if (targetWidth !== pixelCrop.width && targetHeight !== pixelCrop.height) {
          // Needs resize
          resolve(resizeImage(file, targetWidth, targetHeight));
        } else {
          // Done
          resolve(file);
        }
      },
      'image/jpeg',
      1
    );
  });
}

async function resizeImage(file, targetWidth, targetHeight) {
  return new Promise(async (resolve) => {
    // Read Image
    const imageData = await readFile(file);

    // Read Image
    const image = await createImage(imageData);

    // Create a canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Update canvas
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Draw
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Write new file
    canvas.toBlob(
      (resizedFile) => {
        resolve(resizedFile);
      },
      'image/jpeg',
      1
    );
  });
}
